/**
 * EditDepartmentModal.tsx
 * Antd Modal + Form for creating / editing an EPC department master entry.
 * `department === null` → create mode; otherwise edit mode (changed fields only).
 */

import { Form, Input, InputNumber, Modal, Select } from 'antd';
import { toast } from 'react-toastify';

import { masterId } from '../../../services/epcDepartmentMasterService';
import { useDepartmentMasters, type EpcDepartmentMaster } from './useDepartmentMasters';

interface EditDepartmentModalProps {
  /** null = create mode */
  department: EpcDepartmentMaster | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  code?: string;
  focusAreas?: string[];
  sortOrder?: number;
}

export default function EditDepartmentModal({ department, onClose }: EditDepartmentModalProps) {
  const [form] = Form.useForm<FormValues>();
  const { createMutation, updateMutation } = useDepartmentMasters();

  const isCreate = department === null;
  const saving = createMutation.isPending || updateMutation.isPending;

  const handleOk = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // validation errors are shown inline
    }

    try {
      if (isCreate) {
        const result = await createMutation.mutateAsync({
          name: values.name.trim(),
          focusAreas: values.focusAreas?.length ? values.focusAreas : undefined,
        });
        if (result.existed) {
          toast.warning('A department with this name already exists');
          return; // keep the modal open
        }
        // create() only accepts name + focusAreas; persist the extras via update.
        const extras: { code?: string; sortOrder?: number } = {};
        if (values.code?.trim()) extras.code = values.code.trim();
        if (values.sortOrder !== undefined && values.sortOrder !== null) extras.sortOrder = values.sortOrder;
        if (Object.keys(extras).length > 0) {
          try {
            await updateMutation.mutateAsync({ id: result.id, body: extras });
          } catch {
            // Entry was created but the follow-up update failed.  The record
            // exists in the list (invalidation already fired); warn the user
            // and close — retrying "Create" would hit the duplicate path.
            toast.warning('Department created, but code/sort order could not be saved — edit it to retry');
            onClose();
            return;
          }
        }
        toast.success('Department created');
        onClose();
      } else {
        const body: Parameters<typeof updateMutation.mutateAsync>[0]['body'] = {};
        const trimmedName = values.name.trim();
        if (trimmedName !== department.name) body.name = trimmedName;
        if ((values.code?.trim() ?? '') !== (department.code ?? '')) body.code = values.code?.trim() ?? '';
        const nextFocus = values.focusAreas ?? [];
        const prevFocus = department.focusAreas ?? [];
        if (JSON.stringify(nextFocus) !== JSON.stringify(prevFocus)) body.focusAreas = nextFocus;
        if (values.sortOrder !== undefined && values.sortOrder !== null && values.sortOrder !== department.sortOrder) {
          body.sortOrder = values.sortOrder;
        }
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        await updateMutation.mutateAsync({ id: masterId(department), body });
        toast.success('Department updated');
        onClose();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('already exists')) {
        toast.error('Department already exists in master');
      } else {
        toast.error(message || 'Failed to save department');
      }
      // keep the modal open so the user can adjust and retry
    }
  };

  return (
    <Modal
      open
      title={isCreate ? 'Add department' : `Edit ${department.name}`}
      okText={isCreate ? 'Create' : 'Save'}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      destroyOnHidden
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        className="pt-2"
        initialValues={{
          name: department?.name ?? '',
          code: department?.code ?? '',
          focusAreas: department?.focusAreas ?? [],
          sortOrder: department?.sortOrder,
        }}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, whitespace: true, message: 'Department name is required' }]}
        >
          <Input placeholder="e.g. Civil & Structural" autoFocus maxLength={120} />
        </Form.Item>

        <Form.Item name="code" label="Code">
          <Input placeholder="Short code (optional)" maxLength={24} />
        </Form.Item>

        <Form.Item
          name="focusAreas"
          label="Focus areas"
          extra="Free-form tags describing what this department covers."
        >
          <Select mode="tags" placeholder="Type a focus area and press Enter" open={false} tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item name="sortOrder" label="Sort order">
          <InputNumber className="w-full" min={0} placeholder="Lower numbers appear first" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
